import { ICurrentFilter } from '@starryui10/filters/filterBar';
import { INoDataOverlayInfo } from '@starryui10/overlays/NoDataOverlay';
import { ColumnState } from 'ag-grid-community';
import { cloneDeep } from 'lodash';
import { IDirectoryFilter } from '../graphql/useMainDirectoryQuery';
import { strings } from '../localization';

/**
 * Removes the __typename from the grid
 * @param items
 * @returns
 */
export const covertGridFormat = (items: global.GridFormat[] | undefined): global.GridFormat[] => {
    if (items) {
        return items.map((e) => ({
            columnOrder: e.columnOrder,
            columnVisibility: e.columnVisibility,
            columnWidth: e.columnWidth,
            id: e.id,
        }));
    }
    return [];
};

export const convertGridToGraphQL = (grid: ColumnState[]) => {
    return cloneDeep(grid).map((e, i) => ({
        columnOrder: i,
        columnVisibility: !e.hide,
        columnWidth: e.width,
        id: e.colId,
    }));
};

export const buildDirectoryFilter = (currentFilter: ICurrentFilter[] | undefined) => {
    const directoryFilter: IDirectoryFilter[] = [];

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const filterValue = (item: any, useCaption?: boolean) =>
        currentFilter
            ?.filter(item)
            .flatMap((e) => e.value)
            .map((e) => (useCaption ? e!.caption : e!.value));

    if (currentFilter?.length) {
        const applicationStatusIDs = filterValue((e: ICurrentFilter) => e.name === 'Application Status');
        const appliedForTermIDs = filterValue((e: ICurrentFilter) => e.name === 'Applied For Term');
        const bookedForTermIDs = filterValue((e: ICurrentFilter) => e.name === 'Booking For Term');
        const termSessionIDs = filterValue((e: ICurrentFilter) => e.name === 'Active Term Booking');
        const entryStatus = filterValue((e: ICurrentFilter) => e.name === 'Entry Status');
        const genders = filterValue((e: ICurrentFilter) => e.name === 'Gender', true);
        const categoryIDs = filterValue((e: ICurrentFilter) => e.name === 'Category');
        const communityFilters = filterValue((e: ICurrentFilter) => e.name === 'Community');
        const roomTypeIDs = filterValue((e: ICurrentFilter) => e.name === 'Room Types');
        const eventIDs = filterValue((e: ICurrentFilter) => e.name === 'Upcoming Events');
        const groupIDs = filterValue((e: ICurrentFilter) => e.name === 'Upcoming Groups');

        if (applicationStatusIDs?.length) {
            directoryFilter.push({ applicationStatusID: { in: applicationStatusIDs } });
        }
        if (appliedForTermIDs?.length) {
            directoryFilter.push({ appliedForTermID: { in: appliedForTermIDs } });
        }
        if (bookedForTermIDs?.length) {
            directoryFilter.push({ bookedForTermID: { in: bookedForTermIDs } });
        }
        if (termSessionIDs?.length) {
            directoryFilter.push({ termSessionID: { in: termSessionIDs } });
        }
        if (entryStatus?.length) {
            directoryFilter.push({ entryStatus: { in: entryStatus } });
        }
        if (genders?.length) {
            directoryFilter.push({ gender: { in: genders } });
        }
        if (categoryIDs?.length) {
            directoryFilter.push({ categoryID: { in: categoryIDs } });
        }
        if (communityFilters?.length) {
            directoryFilter.push({ communityFilter: { in: communityFilters } });
        }
        if (roomTypeIDs?.length) {
            directoryFilter.push({ roomTypeID: { in: roomTypeIDs } });
        }
        if (eventIDs?.length) {
            directoryFilter.push({ eventID: { in: eventIDs } });
        }
        if (groupIDs?.length) {
            directoryFilter.push({ groupID: { in: groupIDs } });
        }
    }

    return directoryFilter;
};

export const getSearchString = (currentFilters: IDirectoryFilter[] | undefined): string => {
    const searchString = currentFilters?.filter((f) => f.search)[0]?.search?.like;
    return searchString || '';
};

export const noResultsMessage = (filters: IDirectoryFilter[] | undefined, searchString: string): INoDataOverlayInfo | null => {
    if (!filters) return null;

    const hasSearchString = searchString?.length;

    const noItemsInfo = {
        noItemsTitle: strings.MainDirectory_Label_NoneMatchSearchFilterTitle,
        noItemsDescription: strings.MainDirectory_Label_NoneMatchSearchFilterDescription,
        noItemsGraphic: 'SearchEntry' as image.SvgVariants,
    };

    // has filters
    if (!hasSearchString) {
        noItemsInfo.noItemsTitle = strings.MainDirectory_Label_NoneMatchFilterCriteriaTitle;
        noItemsInfo.noItemsDescription = strings.MainDirectory_Label_NoneMatchFilterCriteriaDescription;
    }

    // has searchString
    if (filters.length === 1 && hasSearchString) {
        noItemsInfo.noItemsTitle = strings.MainDirectory_Label_NoneMatchSearchTitle;
        noItemsInfo.noItemsDescription = strings.MainDirectory_Label_NoneMatchSearchDescription;
    }

    // has searchString and filters
    if (filters.length > 1 && hasSearchString) {
        noItemsInfo.noItemsTitle = strings.MainDirectory_Label_NoneMatchSearchFilterTitle;
        noItemsInfo.noItemsDescription = strings.MainDirectory_Label_NoneMatchSearchFilterDescription;
    }

    return noItemsInfo;
};